import { IonIcon } from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import { AnimatePresence, motion } from "motion/react";
import React, { useMemo } from "react";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { resolveUiSpring } from "./motion/uiMotion.tools";

/**
 * Props of the shared full-screen mobile / centered desktop sheet modal.
 */
export interface SheetModalProps {
    "isOpen":   boolean;
    "onClose":  () => void;
    "isDarkUi": boolean;

    /** Title displayed in the sheet header. */
    "title": string;

    /** When `false`, the close control is disabled (e.g. payment in flight). */
    "canClose"?: boolean;

    /** Optional content rendered at the left of the header row (e.g. invoice action). */
    "headerLeading"?: React.ReactNode;

    /** Optional content rendered between the header and the body (e.g. stepper). */
    "headerExtra"?: React.ReactNode;
    "children":     React.ReactNode;

    /** Extra classes on the sheet panel (`motion.div`). */
    "panelClassName"?: string;

    /** Extra classes on the scrollable content wrapper. */
    "contentClassName"?: string;

    /** Extra style on the header band (title row + `headerExtra`). */
    "headerStyle"?: React.CSSProperties;
}

/**
 * Shared sheet modal: bottom-sheet on mobile, centered dialog on desktop.
 *
 * @remarks Extracted from the ticket purchase wizard so independent flows
 * (purchase, ticket viewing, …) share the same chrome without sharing state.
 * @param props - Sheet modal props.
 * @returns The sheet tree; `AnimatePresence` keeps it alive during exit.
 */
export const SheetModal: React.FC<SheetModalProps> = ( {
    isOpen,
    onClose,
    isDarkUi,
    title,
    canClose = true,
    headerLeading,
    headerExtra,
    children,
    panelClassName = "",
    contentClassName = "",
    headerStyle,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const reducedMotion = useReducedMotion();

    // The whole overlay (backdrop + panel) is the animated child so closing plays the same animation backwards.
    return (
        <AnimatePresence>
            {isOpen
                ? (
                    <motion.div
                        animate={{ "opacity": 1 }}
                        className="fixed inset-0 z-50 flex items-end justify-center md:items-center"
                        exit={{ "opacity": 0 }}
                        initial={{ "opacity": 0 }}
                        style={{ "backgroundColor": palette.modalBackdropBackground }}
                        transition={resolveUiSpring( reducedMotion, "sheet" )}
                    >
                        <motion.div
                            animate={{
                                "y":       0,
                                "opacity": 1,
                            }}
                            className={`relative flex h-full w-full flex-col overflow-hidden md:h-[min(90vh,800px)] md:w-[720px] md:rounded-2xl md:border ${ panelClassName }`}
                            exit={{
                                "y":          "100%",
                                "opacity":    0,
                                "transition": resolveUiSpring( reducedMotion, "sheet" ),
                            }}
                            initial={{
                                "y":       "100%",
                                "opacity": 0.96,
                            }}
                            style={{
                                "backgroundColor": palette.modalCardBackground,
                                "borderColor":     palette.modalCardBorder,
                                "color":           palette.modalCardTextPrimary,
                            }}
                            transition={resolveUiSpring( reducedMotion, "sheet" )}
                        >
                            <div
                                className="shrink-0"
                                style={headerStyle}
                            >
                                <div
                                    className="relative flex min-h-[3.5rem] items-center justify-center border-b p-2"
                                    style={{ "borderColor": palette.overlayBorder }}
                                >
                                    <h2 className="text-base font-bold tracking-tight">
                                        {title}
                                    </h2>
                                    {headerLeading !== undefined && headerLeading !== null
                                        ? (
                                            <div className="absolute left-3.5 top-1/2 z-10 flex -translate-y-1/2 items-center gap-1.5">
                                                {headerLeading}
                                            </div>
                                        )
                                        : null}
                                    <button
                                        aria-label="Fermer"
                                        className="absolute right-3.5 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 shrink-0 items-center justify-center rounded-full border border-solid p-0 backdrop-blur-sm transition-transform duration-150 ease-out active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:opacity-40"
                                        disabled={!canClose}
                                        onClick={onClose}
                                        style={{
                                            "borderColor": palette.overlayBorderStrong,
                                            "background":  isDarkUi ? palette.overlayCardBackgroundStrong : palette.overlayCardBackgroundSoft,
                                            "color":       `color-mix(in srgb, ${ palette.overlayTextSecondary } 68%, ${ isDarkUi ? palette.overlayCardBackgroundStrong : palette.overlayCardBackgroundSoft })`,
                                        }}
                                        type="button"
                                    >
                                        <IonIcon
                                            aria-hidden="true"
                                            icon={closeOutline}
                                            style={{ "fontSize": "16px" }}
                                        />
                                    </button>
                                </div>
                                {headerExtra}
                            </div>
                            <div className={`relative flex-1 overflow-hidden ${ contentClassName }`}>
                                {children}
                            </div>
                        </motion.div>
                    </motion.div>
                )
                : null}
        </AnimatePresence>
    );
};
